import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { getEventsList, getUserSelectedEvents } from '@/routes/trackings/tracking-utils';
import { IntegrationV2Query, IntegrationTypeV2, WebhookCredentialsModel } from '@/gql/graphql';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { INTEGRATION_V2_QUERY } from '@/queries/integration';
import { useState, useEffect } from 'react';
import { SaveButton } from '../../components/save-button';
import { FormLayout, FormSection, InputField } from '../../components/tracking-details-form/base';
import {
  ConsentModeSection,
  FormHeaderSection,
  CustomEventsSection
} from '../../components/tracking-details-form/shared-sections';

import { useIntegrationUpdater } from '../../hooks/integration-updater';
import { WebhookSigningSecretCallout } from './webhook-signing-secret-callout';

const formSchema = z.object({
  webhookUrl: z
    .string()
    .min(1, 'Webhook URL is required.')
    .refine((u) => {
      try {
        return new URL(u).protocol === 'https:';
      } catch {
        return false;
      }
    }, 'Webhook URL must be a valid https URL.'),
  consentMode: z.boolean(),
  customEvents: z.array(z.string()).optional()
});

const eventsList = getEventsList(IntegrationTypeV2.Webhook);

export const WebhookSettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { toast } = useToast();
  const params = useParams<{ id: string; slug: string }>();
  const [isUpdating, setIsUpdating] = useState(false);
  // Plaintext signing secret returned exactly once after a save/rotation. Held
  // in component state only — never refetched, never persisted readably.
  const [oneTimeSigningSecret, setOneTimeSigningSecret] = useState<string | null>(null);
  const { updateIntegration } = useIntegrationUpdater();

  const { data } = useQuery<IntegrationV2Query>(INTEGRATION_V2_QUERY, {
    variables: { id: params.id! }
  });

  const existingUrl = (data?.integrationV2.credentials as WebhookCredentialsModel | undefined)
    ?.webhookUrl;
  const isInitialSetup = !existingUrl;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      webhookUrl: '',
      consentMode: false,
      customEvents: []
    }
  });

  useEffect(() => {
    if (!isUpdating && data?.integrationV2) {
      const integration = data.integrationV2;
      const credentials = integration.credentials as WebhookCredentialsModel | undefined;
      form.reset({
        webhookUrl: credentials?.webhookUrl ?? '',
        consentMode: integration.consentMode ?? false,
        customEvents: getUserSelectedEvents(data)
      });
    }
  }, [data, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsUpdating(true);
      const result = await updateIntegration({
        id: params.id!,
        credentials: {
          webhookUrl: values.webhookUrl
        },
        customEvents: values.customEvents ?? [],
        consentMode: values.consentMode
      });
      const secret = result?.data?.updateIntegrationCredentialsV2.oneTimeSigningSecret ?? null;
      if (secret) {
        setOneTimeSigningSecret(secret);
      }
      toast({
        title: 'Form submitted',
        description: 'Your Webhook integration settings have been saved.',
        variant: 'default'
      });
      // When a fresh signing secret was issued, leave the user on this page so
      // they can copy it from the callout — the secret is shown exactly once.
      // The callout has a "I've copied my secret, continue" button that calls
      // onContinue. Auto-advancing here would steal the secret out from under
      // them.
      if (!secret) {
        onContinue();
      }
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to save Webhook integration settings.',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Webhook Settings">
            We will POST a signed JSON payload to this URL for each selected event. The endpoint
            must be publicly reachable over https.
          </FormHeaderSection>
          <FormSection columns={2}>
            <InputField
              form={form}
              name="webhookUrl"
              label="Webhook URL"
              placeholder="https://your-domain.com/contextual/webhook"
            />
          </FormSection>
          {oneTimeSigningSecret && (
            <WebhookSigningSecretCallout
              secret={oneTimeSigningSecret}
              onAcknowledge={() => {
                setOneTimeSigningSecret(null);
                onContinue();
              }}
            />
          )}
          <ConsentModeSection form={form} />
          <CustomEventsSection
            form={form}
            eventsList={eventsList}
            getUserSelectedEvents={() => getUserSelectedEvents(data)}
          />
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isUpdating} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};
